import { defineStore } from "pinia";
import { useSingleSearchStore } from "./eplex/eplexSingleSearch";
import { useGuidanceStore } from "./guidance";
import { useBillingStore } from "./eplex/eplexBilling";
import i18n from "../../i18n";
import { unifiedLogout, unifiedLogoutAll } from "../api/clientContextApi";
import { useClientContextStore } from "./eplex/eplexClientContext";
import { useEplexAccountInfoStore } from "./eplex/eplexAccountInfo";

interface AuthState {
  isLoggingOut: boolean;
}

export const useAuthStore = defineStore("auth", {
  state: (): AuthState => ({
    isLoggingOut: false,
  }),
  actions: {
    async logout(): Promise<void> {
      if (this.isLoggingOut) return;

      this.isLoggingOut = true;

      try {
        // Куки гасит бэкенд: eplex_refresh с HttpOnly фронту недоступен
        await unifiedLogout();
      } catch (error) {
        console.error("Logout error: ", error);
      } finally {
        localStorage.removeItem("_user");
        localStorage.removeItem("access_token");
        localStorage.removeItem("minTime");
        localStorage.removeItem("maxTime");

        const singleSearchStore = useSingleSearchStore();
        singleSearchStore.clearAll();

        const guidanceStore = useGuidanceStore();
        guidanceStore.reset();

        // Биллинг хранит client_id/баланс/профили конкретного пользователя — при выходе
        // обязательно чистим, иначе следующий вход в этой же вкладке унаследует чужой контекст (CAB-629).
        useBillingStore().reset();

        // Контекст клиента несёт демо-доступ и имя — у следующего пользователя
        // в этой же вкладке они свои
        useClientContextStore().reset();
        // Схема тарификации и участие в онбординге — тоже признаки конкретного человека
        useEplexAccountInfoStore().reset();

        // состояние проценки при логауте НЕ чистим: оно хранится по пользователям
        // (ключ `${SAVE_KEY}.<email>`), у следующего пользователя — свой ключ

        this.isLoggingOut = false;

        // Полная перезагрузка, а не SPA-переход: сторы модулей остаются в памяти со
        // всем, что успел набрать прошлый человек, и перечислять их поимённо здесь
        // означает ловить следующий забытый через месяц. Куки к этому моменту уже
        // погашены ответом бэкенда, так что страница входа откроется чистой.
        window.location.assign("/login");
      }
    },

    /**
     * «Завершить сеансы на всех устройствах» — отзыв токенов, а не сброс своих
     * сессий: логин и пароль для него не нужны, человек уже подтверждён кукой.
     */
    async resetAllDevices(): Promise<{ status: string; message: string }> {
      await unifiedLogoutAll();

      return { status: "ok", message: i18n.global.t("auth.sessionsTerminated") };
    },
  },
});
